// src/components/AuthForm.jsx
import React, { useState } from "react";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  CognitoUser,
  AuthenticationDetails,
  CognitoUserAttribute,
} from "amazon-cognito-identity-js";
import { useTheme } from "../theming";
import { userPool } from "../awsConfig";
import { getUserInfoFromToken } from "../utils/authUtils";
import { updateLibraryCardRequest } from "../actions/libraryCardActions";

const AuthForm = ({ setIsAuthenticated, updateLibraryCardRequest }) => {
  const [isRegister, setIsRegister] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    email: "",
    resetCode: "",
    newPassword: "",
  });
  const [message, setMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [showPasswords, setShowPasswords] = useState({});

  const navigate = useNavigate();
  const theme = useTheme();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const errors = {};
    const { username, password, email } = formData;
    if (!username || username.length < 3)
      errors.username = "Username must be at least 3 characters.";
    if (!password || password.length < 8)
      errors.password = "Password must be at least 8 characters.";
    else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(password))
      errors.password = "Password must include uppercase, lowercase, and a number.";
    if (isRegister) {
      if (!email) errors.email = "Email is required.";
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
        errors.email = "Please enter a valid email address.";
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSignUp = (e) => {
    e.preventDefault();
    setMessage(null);
    if (!validateForm()) return;
    setIsLoading(true);
    const { username, password, email } = formData;
    const emailAttr = new CognitoUserAttribute({ Name: "email", Value: email });

    userPool.signUp(username, password, [emailAttr], null, (err) => {
      setIsLoading(false);
      if (err) {
        const msgs = {
          UsernameExistsException: "Username already exists.",
          InvalidPasswordException: "Password does not meet requirements.",
          InvalidParameterException: "Invalid input.",
          TooManyRequestsException: "Too many requests. Please try again later.",
        };
        setMessage(`Registration failed: ${msgs[err.code] || err.message}`);
        return;
      }
      setMessage(`Registered! Check ${email} for your confirmation code.`);
      localStorage.setItem("pendingUsername", username);
      setTimeout(() => navigate("/confirm-signup"), 2000);
    });
  };

  const handleSignIn = (e) => {
    e.preventDefault();
    const { username, password } = formData;

    const cognitoUser = new CognitoUser({ Username: username, Pool: userPool });
    const authDetails = new AuthenticationDetails({ Username: username, Password: password });

    cognitoUser.authenticateUser(authDetails, {
      onSuccess: (result) => {
        const idToken = result.getIdToken().getJwtToken();
        const accessToken = result.getAccessToken().getJwtToken();
        localStorage.setItem("jwtToken", idToken);
        localStorage.setItem("accessToken", accessToken);

        setIsAuthenticated(true);

        // tenantId is the Cognito sub from the JWT — no per-user setup required.
        // Display name (optional) is captured later via BrandCreatorProfileKit.
        navigate("/");
      },
      onFailure: (err) => {
        setMessage(`Login failed: ${err.message}`);
      },
    });
  };

  const handleForgotPassword = (e) => {
    e.preventDefault();
    const cognitoUser = new CognitoUser({ Username: formData.username, Pool: userPool });
    cognitoUser.forgotPassword({
      onSuccess: () => {
        setMessage("Reset code sent to your email.");
        setIsForgotPassword(true);
      },
      onFailure: (err) => setMessage(`Error: ${err.message}`),
    });
  };

  const handleResendCode = () => {
    if (!formData.username) {
      setMessage("Please enter your username first.");
      return;
    }
    const cognitoUser = new CognitoUser({ Username: formData.username, Pool: userPool });
    cognitoUser.forgotPassword({
      onSuccess: () => setMessage("A new code has been sent to your email."),
      onFailure: (err) => setMessage(`Error: ${err.message}`),
    });
  };

  const handleResetPassword = (e) => {
    e.preventDefault();
    const { username, resetCode, newPassword } = formData;
    const cognitoUser = new CognitoUser({ Username: username, Pool: userPool });
    cognitoUser.confirmPassword(resetCode, newPassword, {
      onSuccess: () => {
        setMessage("Password reset! You can now log in.");
        setIsForgotPassword(false);
      },
      onFailure: (err) => setMessage(`Error: ${err.message}`),
    });
  };

  const inputClass = (field) =>
    `w-full px-3 py-2 mt-1 border rounded-md focus:outline-none focus:ring-2 ${
      validationErrors[field] ? "border-red-500" : ""
    }`;

  const toggleShow = (field) =>
    setShowPasswords((prev) => ({ ...prev, [field]: !prev[field] }));

  const EyeIcon = ({ field }) => (
    <button
      type="button"
      onClick={() => toggleShow(field)}
      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
      tabIndex={-1}
    >
      {showPasswords[field] ? (
        // Eye-off
        <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
        </svg>
      ) : (
        // Eye
        <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      )}
    </button>
  );

  const btnStyle = {
    backgroundColor: theme.accentColor,
    color: theme.buttonTextColor,
  };

  return (
    <div
      className="flex flex-col items-center justify-center min-h-screen"
      style={{ backgroundColor: theme.backgroundColor }}
    >
      <div className="w-full max-w-md p-8 space-y-5 bg-white rounded-xl shadow-lg">
        <h2
          className="text-3xl font-serif font-semibold text-center"
          style={{ color: theme.accentColor }}
        >
          {isRegister ? "Create Account" : isForgotPassword ? "Reset Password" : "Welcome Back"}
        </h2>
        <p className="text-center text-sm" style={{ color: theme.textColor }}>
          {isRegister
            ? "Artist account registration"
            : isForgotPassword
            ? "Enter your username and we'll send a reset code"
            : "Sign in to manage your gallery"}
        </p>

        {/* Forgot password form */}
        {isForgotPassword ? (
          <form onSubmit={handleResetPassword} className="space-y-4">
            {[
              { label: "Username", name: "username", type: "text" },
              { label: "Reset Code", name: "resetCode", type: "text" },
              { label: "New Password", name: "newPassword", type: "password" },
            ].map(({ label, name, type }) => (
              <div key={name}>
                <label className="block text-sm font-medium">{label}</label>
                <div className="relative">
                  <input
                    type={type === "password" && showPasswords[name] ? "text" : type}
                    name={name}
                    value={formData[name]}
                    onChange={handleChange}
                    className={inputClass(name) + (type === "password" ? " pr-10" : "")}
                    required
                  />
                  {type === "password" && <EyeIcon field={name} />}
                </div>
              </div>
            ))}
            <button type="submit" className="w-full py-2 font-semibold rounded-md" style={btnStyle}>
              Reset Password
            </button>
            <div className="text-center">
              <button
                type="button"
                onClick={handleResendCode}
                className="text-sm underline"
                style={{ color: theme.accentColor }}
              >
                Didn't receive a code? Resend
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={isRegister ? handleSignUp : handleSignIn} className="space-y-4">
            {[
              { label: "Username", name: "username", type: "text" },
              { label: "Password", name: "password", type: "password" },
              ...(isRegister ? [{ label: "Email", name: "email", type: "email" }] : []),
            ].map(({ label, name, type }) => (
              <div key={name}>
                <label className="block text-sm font-medium">{label}</label>
                <div className="relative">
                  <input
                    type={type === "password" && showPasswords[name] ? "text" : type}
                    name={name}
                    value={formData[name]}
                    onChange={handleChange}
                    className={inputClass(name) + (type === "password" ? " pr-10" : "")}
                    required
                  />
                  {type === "password" && <EyeIcon field={name} />}
                </div>
                {validationErrors[name] && (
                  <p className="text-red-500 text-xs mt-1">{validationErrors[name]}</p>
                )}
              </div>
            ))}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-2 font-semibold rounded-md disabled:opacity-50 flex items-center justify-center"
              style={btnStyle}
            >
              {isLoading ? "Please wait…" : isRegister ? "Register" : "Sign In"}
            </button>
          </form>
        )}

        {message && (
          <p className="text-sm text-center" style={{ color: theme.textColor }}>
            {message}
          </p>
        )}

        {!isForgotPassword && !isRegister && (
          <div className="text-center">
            <button
              onClick={() => { setIsForgotPassword(true); setMessage(null); }}
              className="text-sm underline"
              style={{ color: theme.accentColor }}
            >
              Forgot password?
            </button>
          </div>
        )}

        <div className="text-center">
          <button
            onClick={() => { setIsRegister(!isRegister); setIsForgotPassword(false); setMessage(null); }}
            className="text-sm underline"
            style={{ color: theme.accentColor }}
          >
            {isRegister ? "Already have an account? Sign in" : "Need an account? Register"}
          </button>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = () => ({});
const mapDispatchToProps = (dispatch) => ({
  updateLibraryCardRequest: (payload) => dispatch(updateLibraryCardRequest(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AuthForm);
